import usePrivateLink from "../../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Footer from "../footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

import {
  getTestSyllabus,
  saveTestSyllabus,
  deleteTestSyllabus,
  notifyAllTestSyllabus,
} from "../../../api/TestService";

import {
  formatDate,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../../master/Master";

const emptyChapter = () => ({ chapter: "", topicsText: "" });

const getErrorMessage = (error) => {
  const data = error?.response?.data;

  // Laravel validation errors (422)
  if (data?.errors) {
    return Object.values(data.errors).flat()[0];
  }

  return data?.message ?? error?.response?.message ?? error?.message;
};

export default function TestSyllabus() {
  const hasAccess = usePrivateLink();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testId = searchParams.get("id");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [test, setTest] = useState(null);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allAdded, setAllAdded] = useState(false);

  // modal
  const [showModal, setShowModal] = useState(false);
  const [activeSubject, setActiveSubject] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    chapters: [emptyChapter()],
    notify: false,
  });

  /* =========================================================
     FETCH
  ========================================================= */

  const fetchSyllabus = async () => {
    if (!testId) return;

    setLoading(true);

    try {
      const response = await getTestSyllabus(testId);

      if (response?.data?.status) {
        const d = response.data.data;

        setTest(d.test);
        setBatches(d.batches || []);
        setSubjects(d.subjects || []);
        setAllAdded(!!d.all_added);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSyllabus();
  }, [testId]);

  const addedCount = subjects.filter((s) => s.syllabus).length;
  const progress = subjects.length
    ? Math.round((addedCount / subjects.length) * 100)
    : 0;
  const batchNames = batches.map((b) => b.title).join(", ");

  /* =========================================================
     MODAL / FORM
  ========================================================= */

  const openModal = (subject) => {
    const s = subject.syllabus;

    setActiveSubject(subject);

    setForm({
      title: s?.title || "",
      description: s?.description || "",
      chapters:
        s?.topics?.length > 0
          ? s.topics.map((t) => ({
              chapter: t.chapter || "",
              topicsText: (t.topics || []).join("\n"),
            }))
          : [emptyChapter()],
      notify: false,
    });

    setShowModal(true);
  };

  const closeModalLocal = () => {
    setShowModal(false);
    setActiveSubject(null);
  };

  const updateChapter = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      chapters: prev.chapters.map((c, i) =>
        i === index ? { ...c, [key]: value } : c,
      ),
    }));
  };

  const addChapter = () => {
    setForm((prev) => ({
      ...prev,
      chapters: [...prev.chapters, emptyChapter()],
    }));
  };

  const removeChapter = (index) => {
    setForm((prev) => {
      const next = prev.chapters.filter((_, i) => i !== index);

      return { ...prev, chapters: next.length ? next : [emptyChapter()] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Drop completely empty rows, convert textarea -> array
    const rows = form.chapters.filter(
      (c) => c.chapter.trim() || c.topicsText.trim(),
    );

    if (rows.some((c) => !c.chapter.trim())) {
      SweetAlertShow(
        "info",
        "Missing Chapter",
        "Enter a chapter name for each row.",
      );
      return;
    }

    const topics = rows.map((c) => ({
      chapter: c.chapter.trim(),
      topics: c.topicsText
        .split("\n")
        .map((t) => t.trim())
        .filter(Boolean),
    }));

    if (!form.description.trim() && topics.length === 0) {
      SweetAlertShow(
        "info",
        "Syllabus is empty",
        "Add a description or at least one chapter.",
      );
      return;
    }

    setSaving(true);

    try {
      const response = await saveTestSyllabus(testId, {
        subject_id: activeSubject.subject_id,
        title: form.title.trim() || null,
        description: form.description.trim() || null,
        topics: topics.length ? topics : null,
        notify: form.notify ? 1 : 0,
      });

      if (response?.data?.status) {
        ToastMain.fire({
          icon: "success",
          title: form.notify
            ? response.data.notified
              ? `${response.data.message} • notified ${response.data.sent} of ${response.data.recipients} students`
              : `${response.data.message} • but no student could be notified`
            : response.data.message,
        });

        closeModalLocal();
        fetchSyllabus();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = (subject) => {
    Swal.fire({
      title: "Remove syllabus?",
      text: `Remove the syllabus of "${subject.subject_name || "this subject"}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove it",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setLoading(true);

      try {
        const response = await deleteTestSyllabus(testId, subject.subject_id);

        if (response?.data?.status) {
          ToastMain.fire({ icon: "success", title: "Syllabus removed" });
          fetchSyllabus();
        } else {
          SweetAlertShow("info", "Warning", response?.data?.message);
        }
      } catch (error) {
        SweetAlertShow("error", "Error", getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    });
  };

  /* =========================================================
     NOTIFY ALL
  ========================================================= */

  const handleNotifyAll = () => {
    Swal.fire({
      title: "Notify students?",
      text: `Send the syllabus update to all students of: ${
        batchNames || "the selected batches"
      }.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, notify",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setLoading(true);

      try {
        const response = await notifyAllTestSyllabus(testId);

        if (response?.data?.status) {
          ToastMain.fire({
            icon: "success",
            title: `Notified ${response.data.sent} of ${response.data.recipients} students`,
          });
          fetchSyllabus();
        } else {
          SweetAlertShow("info", "Warning", response?.data?.message);
        }
      } catch (error) {
        SweetAlertShow("error", "Error", getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    });
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      {(loading || saving) && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-3">
                <div className="row">
                  <div className="col-12">
                    {/* ================= PAGE HEADER ================= */}

                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center">
                            <div
                              className="bg-label-primary rounded-3 d-flex align-items-center justify-content-center me-3"
                              style={{ width: 48, height: 48, minWidth: 48 }}
                            >
                              <i className="ri-book-open-line fs-3 text-primary"></i>
                            </div>

                            <div>
                              <h4 className="fw-bold mb-1">Exam Syllabus</h4>

                              <p className="text-muted mb-0">
                                {test?.title || "—"}
                                {test?.date
                                  ? ` • ${formatDate(test.date)}`
                                  : ""}
                              </p>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => navigate(-1)}
                            >
                              <i className="ri-arrow-left-line me-1"></i>
                              Back
                            </button>

                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={!allAdded}
                              onClick={handleNotifyAll}
                              title={
                                allAdded
                                  ? "Notify batch students"
                                  : "Add the syllabus of all subjects first"
                              }
                            >
                              <i className="ri-notification-3-line me-1"></i>
                              Notify All Students
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ================= PROGRESS ================= */}

                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                          <div>
                            <h6 className="fw-semibold mb-0">
                              Syllabus Progress
                            </h6>

                            <small className="text-muted">
                              {addedCount} of {subjects.length} subjects added
                            </small>
                          </div>

                          {batches.length > 0 && (
                            <small className="text-muted">
                              <i className="ri-group-line me-1"></i>
                              Batches: <strong>{batchNames}</strong>
                            </small>
                          )}
                        </div>

                        <div className="progress" style={{ height: 8 }}>
                          <div
                            className={`progress-bar ${
                              allAdded ? "bg-success" : "bg-primary"
                            }`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>

                        {!allAdded && subjects.length > 0 && (
                          <small className="text-muted d-block mt-2">
                            <i className="ri-information-line me-1"></i>
                            "Notify All Students" unlocks once every subject has
                            a syllabus.
                          </small>
                        )}
                      </div>
                    </div>

                    {/* ================= SUBJECT LIST ================= */}

                    <div className="card border-0 shadow-sm">
                      <div className="card-body border-bottom py-3 px-4">
                        <h5 className="fw-semibold mb-0">Subjects</h5>

                        <small className="text-muted">
                          Add or update the syllabus of each subject.
                        </small>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr>
                              <th className="ps-4" style={{ width: 70 }}>
                                #
                              </th>
                              <th>Subject</th>
                              <th>Syllabus</th>
                              <th>Status</th>
                              <th
                                className="text-end pe-4"
                                style={{ width: 180 }}
                              >
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {subjects.length > 0 ? (
                              subjects.map((item, index) => {
                                const s = item.syllabus;
                                const chapterCount = s?.topics?.length || 0;

                                return (
                                  <tr key={item.subject_id}>
                                    <td className="ps-4">
                                      <span className="text-muted fw-medium">
                                        {String(index + 1).padStart(2, "0")}
                                      </span>
                                    </td>

                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div
                                          className="bg-label-primary rounded-2 d-flex align-items-center justify-content-center me-3"
                                          style={{
                                            width: 38,
                                            height: 38,
                                            minWidth: 38,
                                          }}
                                        >
                                          <i className="ri-book-2-line text-primary"></i>
                                        </div>

                                        <h6 className="mb-0 fw-semibold">
                                          {item.subject_name ||
                                            `Subject #${item.subject_id}`}
                                        </h6>
                                      </div>
                                    </td>

                                    <td>
                                      {s ? (
                                        <div>
                                          <div className="fw-medium">
                                            {s.title ||
                                              (chapterCount > 0
                                                ? `${chapterCount} chapter${chapterCount > 1 ? "s" : ""}`
                                                : "Description only")}
                                          </div>

                                          {s.description && (
                                            <small className="text-muted">
                                              {s.description.length > 60
                                                ? s.description.substring(
                                                    0,
                                                    60,
                                                  ) + "..."
                                                : s.description}
                                            </small>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>

                                    <td>
                                      {s ? (
                                        <div className="d-flex flex-column align-items-start gap-1">
                                          <span className="badge bg-label-success rounded-pill">
                                            Added
                                          </span>

                                          {s.notified_at && (
                                            <small className="text-muted">
                                              <i className="ri-notification-3-line me-1"></i>
                                              Notified{" "}
                                              {formatDate(s.notified_at)}
                                            </small>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="badge bg-label-secondary rounded-pill">
                                          Not added
                                        </span>
                                      )}
                                    </td>

                                    <td className="text-end pe-4">
                                      <div className="d-flex justify-content-end gap-2">
                                        <button
                                          type="button"
                                          className={`btn btn-sm ${
                                            s
                                              ? "btn-outline-primary"
                                              : "btn-primary"
                                          }`}
                                          onClick={() => openModal(item)}
                                        >
                                          <i
                                            className={`${
                                              s ? "ri-edit-line" : "ri-add-line"
                                            } me-1`}
                                          ></i>
                                          {s ? "Update" : "Add"}
                                        </button>

                                        {s && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-outline-danger"
                                            title="Remove syllabus"
                                            onClick={() => handleDelete(item)}
                                          >
                                            <i className="ri-delete-bin-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center">
                                  <div className="py-5">
                                    <div
                                      className="bg-label-primary rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                                      style={{ width: 70, height: 70 }}
                                    >
                                      <i className="ri-book-open-line fs-1 text-primary"></i>
                                    </div>

                                    <h5 className="fw-semibold mb-2">
                                      No Subjects Found
                                    </h5>

                                    <p className="text-muted mb-0">
                                      This test has no subjects to add a
                                      syllabus to.
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Footer />
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>

      {/* =========================================================
          ADD / UPDATE SYLLABUS MODAL
      ========================================================== */}

      {showModal && activeSubject && (
        <div
          className="modal show fade d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow">
              <form
                onSubmit={handleSave}
                className="d-flex flex-column overflow-hidden"
              >
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold mb-1">
                      {activeSubject.syllabus ? "Update" : "Add"} Syllabus
                    </h5>

                    <small className="text-muted">
                      {activeSubject.subject_name ||
                        `Subject #${activeSubject.subject_id}`}{" "}
                      • {test?.title}
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModalLocal}
                  ></button>
                </div>

                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label">Heading (optional)</label>

                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Chapters 1 to 4"
                      maxLength={200}
                      value={form.title}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, title: e.target.value }))
                      }
                    />
                  </div>

                  {/* Chapters */}

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label mb-0">
                        Chapters & Topics
                      </label>

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={addChapter}
                      >
                        <i className="ri-add-line me-1"></i>
                        Add Chapter
                      </button>
                    </div>

                    {form.chapters.map((c, i) => (
                      <div key={i} className="border rounded-3 p-3 mb-2">
                        <div className="d-flex gap-2 mb-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Chapter name"
                            maxLength={255}
                            value={c.chapter}
                            onChange={(e) =>
                              updateChapter(i, "chapter", e.target.value)
                            }
                          />

                          <button
                            type="button"
                            className="btn btn-icon btn-outline-danger"
                            title="Remove chapter"
                            onClick={() => removeChapter(i)}
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>

                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Topics (one per line)"
                          value={c.topicsText}
                          onChange={(e) =>
                            updateChapter(i, "topicsText", e.target.value)
                          }
                        ></textarea>
                      </div>
                    ))}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Description / Instructions
                    </label>

                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Any extra notes for students"
                      value={form.description}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, description: e.target.value }))
                      }
                    ></textarea>
                  </div>

                  {/* Notify */}

                  <div className="bg-label-primary rounded-3 p-3">
                    <div className="form-check mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="notifyStudents"
                        checked={form.notify}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, notify: e.target.checked }))
                        }
                      />

                      <label
                        className="form-check-label"
                        htmlFor="notifyStudents"
                      >
                        <span className="fw-semibold">
                          Notify students after saving
                        </span>

                        <small className="d-block text-muted">
                          {batchNames
                            ? `Students of: ${batchNames}`
                            : "Students of the test's batches"}
                        </small>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={closeModalLocal}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1"></i>
                        Save Syllabus
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
